import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StickyNote, Plus, ArrowLeft, X, Edit, Trash2, AlertTriangle, Search, Clock, CheckCircle, XCircle, Phone, Sparkles } from 'lucide-react';
import axios from 'axios';
import { NODE_API_URL } from '../config';

// API_URL is now managed in src/config.js
const API_URL = NODE_API_URL;

const PatientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Data State
  const [patient, setPatient] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [notesList, setNotesList] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);

  // Note Editing State
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNoteContent, setEditedNoteContent] = useState("");

  // Search State
  const [searchTerm, setSearchTerm] = useState("");

  // UI States
  const [showNotes, setShowNotes] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rxToDelete, setRxToDelete] = useState(null);

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);


  // Form State
  const [rxForm, setRxForm] = useState({
    rx_no: '', ndc: '', drug_name: '', strength: '', form: '',
    auth_ref_number: '', last_ref_number: '', expiration_date: '',
    order_date: new Date().toISOString().split('T')[0], last_fill_date: '',
    quantity: '', days_supply: '', amount: '',
    prescriber_name: '', prescriber_phone: '',
    ins_code: '',
    status: 'Pending'
  });

  // ✅ EXACT STATUS LIST
  const STATUS_OPTIONS = [
    "Pending",
    "Appointment First",
    "Call Back",
    "Patient Call",
    "Processing",
    "Request Electronically",
    "Data Not Found",
    "Additional Info",
    "Don't Need",
    "Invalid",
    "Discontinued",
    "Others"
  ];

  // Read import indicators from sessionStorage (shared with ListOfPatients)
  const [recentImport] = useState(() => {
    try {
      const raw = sessionStorage.getItem('list_recent_import');
      if (!raw) return { newRxIds: [], updatedRxIds: [] };
      return JSON.parse(raw);
    } catch { return { newRxIds: [], updatedRxIds: [] }; }
  });
  
  const newRxSet = new Set(recentImport.newRxIds || []);
  const updatedRxSet = new Set(recentImport.updatedRxIds || []);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/patients/${id}?_t=${Date.now()}`);
      setPatient(response.data.patient);
      setPrescriptions(response.data.prescriptions || []);
      setNotesList(response.data.notes || []);
      setLoading(false);
    } catch (error) {
      console.error("Error loading profile:", error);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ✅ HELPER: Color Logic
  const getStatusBadge = (status) => {
    const s = (status || 'Pending').toUpperCase();

    if (s.includes('FOLLOW UP') || s === 'F') return 'bg-teal-100 text-teal-700 border border-teal-200';
    if (s.includes('BILLING') || s === 'B') return 'bg-orange-100 text-orange-700 border border-orange-200';
    if (s.includes('URGENT') || s === 'U') return 'bg-red-100 text-red-700 border border-red-200';
    
    if (s.includes('NO ANSWER')) return 'bg-red-50 text-red-600 border border-red-100';
    if (s.includes('VOICEMAIL')) return 'bg-yellow-50 text-yellow-600 border border-yellow-100';
    if (s.includes('EMAIL')) return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
    if (s.includes('FAX')) return 'bg-cyan-100 text-cyan-700 border border-cyan-200';
    if (s.includes('APPOINTMENT')) return 'bg-sky-100 text-sky-700 border border-sky-200';
    if (s.includes('CALL BACK')) return 'bg-purple-100 text-purple-700 border border-purple-200';
    if (s.includes('PATIENT CALL')) return 'bg-orange-100 text-orange-700 border border-orange-200';
    if (s.includes('REQUEST ELECTRONICALLY') || s.includes('ELECTRONIC')) return 'bg-pink-100 text-pink-700 border border-pink-200';
    if (s.includes('DATA NOT FOUND')) return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    if (s.includes('ADDITIONAL INFO')) return 'bg-blue-100 text-blue-700 border border-blue-200';
    if (s.includes("DON'T NEED")) return 'bg-gray-700 text-white border border-gray-800';
    if (s.includes('DISCONTINUED')) return 'bg-red-800 text-white border border-red-900';
    if (s === 'INVALID' || s.includes('INVALID')) return 'bg-red-500 text-white border border-red-600';
    if (s.includes('PROCESSING') || s.includes('PROCESS')) return 'bg-blue-100 text-blue-700 border border-blue-200';
    if (s.includes('COMPLETED')) return 'bg-green-100 text-green-700 border border-green-200';

    return 'bg-gray-100 text-gray-600 border border-gray-200';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '0000-00-00 00:00:00') return '-';

    // Strip time portion if present (e.g., "2026-01-10 00:00:00" or "2026-01-10T00:00:00")
    const cleanDate = String(dateString).split(' ')[0].split('T')[0];

    // Handle YYYY-MM-DD
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(cleanDate)) {
      const [year, month, day] = cleanDate.split('-');
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    }

    // Handle M/D/YYYY or MM/DD/YYYY
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cleanDate)) {
      return cleanDate;
    }

    return dateString;
  };

  const formatNoteDate = (dateString) => {
    if (!dateString) return 'Just now';
    let isoString = dateString.replace(" ", "T");
    const date = new Date(`${isoString}+08:00`);
    if (isNaN(date.getTime())) return 'Just now';
    return date.toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const currentUserId = currentUser ? currentUser.id : null;
    await axios.post(`${API_URL}/notes/add`, { patient_id: id, note_content: newNote, current_user_id: currentUserId });
    setNewNote(""); fetchData();
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Delete this note?")) {
      await axios.post(`${API_URL}/notes/delete`, { id: noteId }); fetchData();
    }
  };

  const startEditNote = (note) => { setEditingNoteId(note.id); setEditedNoteContent(note.note_content); };
  const saveEditedNote = async () => { await axios.post(`${API_URL}/notes/update`, { id: editingNoteId, note_content: editedNoteContent }); setEditingNoteId(null); setEditedNoteContent(""); fetchData(); };
  const cancelEditNote = () => { setEditingNoteId(null); setEditedNoteContent(""); };

  const openAddModal = () => {
    setIsEditing(false); setEditId(null);
    setRxForm({
      rx_no: '', ndc: '', drug_name: '', strength: '', form: '',
      auth_ref_number: '', last_ref_number: '',
      expiration_date: '', order_date: new Date().toISOString().split('T')[0], last_fill_date: '',
      quantity: '', days_supply: '', amount: '',
      prescriber_name: '', prescriber_phone: '',
      ins_code: '',
      status: 'Pending'
    });
    setShowRxModal(true);
  };

  const handleEditClick = (item) => {
    setIsEditing(true); setEditId(item.id);
    const fixDate = (d) => (d && d !== '0000-00-00') ? d.split(' ')[0] : '';

    setRxForm({
      rx_no: item.rx_no || '', ndc: item.ndc || '', drug_name: item.drug_name || '', strength: item.strength || '', form: item.form || '',
      auth_ref_number: item.auth_ref_number || '', last_ref_number: item.last_ref_number || '',
      expiration_date: fixDate(item.expiration_date), order_date: fixDate(item.order_date), last_fill_date: fixDate(item.last_fill_date),
      quantity: item.quantity || '', days_supply: item.days_supply || '', amount: item.amount || '',
      prescriber_name: item.prescriber_name || '', prescriber_phone: item.prescriber_phone || '',
      ins_code: item.ins_code || '',
      status: item.status || 'Pending'
    });
    setShowRxModal(true);
  };

  const confirmDelete = (item) => { setRxToDelete(item); setShowDeleteModal(true); };
  const executeDelete = async () => { if (!rxToDelete) return; try { await axios.post(`${API_URL}/prescriptions/delete`, { id: rxToDelete.id }); setShowDeleteModal(false); fetchData(); } catch (error) { console.error(error); } };

  const handleSaveRx = async (e) => {
    e.preventDefault();

    const endpoint = isEditing ? `${API_URL}/prescriptions/update` : `${API_URL}/prescriptions/add`;
    const payloadStatus = rxForm.status || 'Pending';

    const payload = isEditing
      ? { ...rxForm, status: payloadStatus, id: editId }
      : { ...rxForm, status: payloadStatus, patient_id: id };

    try {
      await axios.post(endpoint, payload);
      alert(isEditing ? "Prescription Updated!" : "Prescription Registered!");
      setShowRxModal(false); fetchData();
    } catch (error) { console.error(error); alert("Error saving prescription"); }
  };

  // ✅ Navigate to Tracker Details using ID
  const handleGoToTracker = (rxId) => {
    navigate(`/tracker/${rxId}`);
  };

  const filteredPrescriptions = prescriptions.filter(item =>
    (item.rx_no && item.rx_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.drug_name && item.drug_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.prescriber_name && item.prescriber_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;
  if (!patient) return <div className="p-10 text-center text-red-500">Patient not found.</div>;

  return (
    <div className="relative">
      <button onClick={() => navigate('/patients')} className="flex items-center text-gray-500 hover:text-teal-600 mb-4">
        <ArrowLeft size={16} className="mr-1" /> Back to List
      </button>

      {/* Profile Card */}
      <div className="bg-white border-l-4 border-teal-600 shadow-md rounded p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div><p className="text-xs text-gray-400 uppercase">Patient Name</p><p className="text-xl font-bold text-gray-800">{patient.full_name}</p></div>
        <div><p className="text-xs text-gray-400 uppercase">Address</p><p className="text-md font-medium text-gray-700 truncate" title={patient.address}>{patient.address || '-'}</p></div>
        <div><p className="text-xs text-gray-400 uppercase">Phone Number</p><p className="text-md font-medium text-gray-700">{patient.phone_number}</p></div>
        <div><p className="text-xs text-gray-400 uppercase">Date of Birth</p><p className="text-md font-medium text-gray-700">{formatDate(patient.dob)}</p></div>
        <div><p className="text-xs text-gray-400 uppercase">Insurance</p><div className="text-md font-bold text-green-600">{patient.insurance_provider}</div></div>
        <div><p className="text-xs text-gray-400 uppercase">Status</p><span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold mt-1 ${patient.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{patient.status === 'Active' ? <CheckCircle size={12} /> : <XCircle size={12} />}{patient.status || 'Active'}</span></div>
      </div>

      {/* Actions Row */}
      <div className="flex justify-between items-center mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input type="text" placeholder="Search History..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="flex gap-3">
          <button onClick={openAddModal} className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 shadow flex items-center gap-2"><Plus size={16} /> Register</button>
          <button onClick={() => setShowNotes(!showNotes)} className={`px-4 py-2 rounded shadow-sm flex items-center gap-2 border transition ${showNotes ? 'bg-yellow-400 text-yellow-900 border-yellow-500 shadow-md' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}><StickyNote size={16} /> {showNotes ? "Hide Notes" : "View Notes"}</button>
        </div>
      </div>

      {/* DATA TABLE */}
      <div className="bg-white shadow rounded-lg overflow-x-auto border border-gray-200">
        <table className="min-w-full text-xs text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th className="px-3 py-3 border-b">RX No.</th>
              <th className="px-3 py-3 border-b">Drugname</th>
              <th className="px-3 py-3 border-b">Auth Ref</th>
              <th className="px-3 py-3 border-b">Last Ref</th>
              <th className="px-3 py-3 border-b">Exp Date</th>
              <th className="px-3 py-3 border-b">Order Date</th>
              <th className="px-3 py-3 border-b">Last Fill</th>
              <th className="px-3 py-3 border-b">Qty</th>
              <th className="px-3 py-3 border-b">Days</th>
              <th className="px-3 py-3 border-b">Amt</th>
              <th className="px-3 py-3 border-b">Prescriber</th>
              <th className="px-3 py-3 border-b">Ins Code</th>
              <th className="px-3 py-3 border-b">Phone</th>
              <th className="px-3 py-3 border-b">Status</th>
              <th className="px-3 py-3 border-b text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPrescriptions.length === 0 ? <tr><td colSpan="13" className="text-center py-6 text-gray-400">No prescriptions found.</td></tr> : filteredPrescriptions.map((item, index) => (
              <tr key={index} className="bg-white border-b hover:bg-gray-50">
                <td className="px-3 py-3 font-medium text-teal-600">{item.rx_no}</td>
                <td className="px-3 py-3 font-bold text-gray-800">
                  <div className="flex items-center gap-2">
                    {item.drug_name}
                    {newRxSet.has(item.id) && (
                      <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide inline-flex items-center gap-1">
                        <Sparkles size={10} /> New
                      </span>
                    )}
                    {!newRxSet.has(item.id) && updatedRxSet.has(item.id) && (
                      <span className="bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide">
                        Updated
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3">{item.auth_ref_number || '-'}</td>
                <td className="px-3 py-3">{item.last_ref_number || '-'}</td>
                <td className="px-3 py-3 text-red-500">{formatDate(item.expiration_date)}</td>
                <td className="px-3 py-3">{formatDate(item.order_date)}</td>
                <td className="px-3 py-3">{formatDate(item.last_fill_date)}</td>
                <td className="px-3 py-3">{item.quantity}</td>
                <td className="px-3 py-3">{item.days_supply}</td>
                <td className="px-3 py-3">${item.amount}</td>
                <td className="px-3 py-3 text-gray-900">{item.prescriber_name}</td>
                <td className="px-3 py-3 font-mono text-[10px] text-blue-600">{item.ins_code || '-'}</td>
                <td className="px-3 py-3 text-gray-900">{item.prescriber_phone || '-'}</td>

                <td className="px-3 py-3">
                  <span className={`px-2 py-1 rounded text-[9px] font-bold text-nowrap uppercase tracking-wide ${getStatusBadge(item.status)}`}>
                    {item.status || 'Pending'}
                  </span>
                </td>

                <td className="px-3 py-3 text-center flex justify-center gap-2 items-center">
                  {/* ✅ NEW: BIGGER & MORE OBVIOUS TRACKER BUTTON */}
                  <button
                    onClick={() => handleGoToTracker(item.id)}
                    className="bg-blue-600 text-white px-3 py-1.5 rounded flex items-center gap-1.5 hover:bg-blue-700 transition shadow-sm text-xs font-bold"
                    title="View Call Logs & Tracker"
                  >
                    <Phone size={14} /> Tracker
                  </button>

                  <button onClick={() => handleEditClick(item)} className="text-teal-500 hover:bg-teal-50 p-1.5 rounded transition border border-transparent hover:border-teal-200"><Edit size={16} /></button>
                  <button onClick={() => confirmDelete(item)} className="text-red-500 hover:bg-red-50 p-1.5 rounded transition border border-transparent hover:border-red-200"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* NOTES HISTORY (Same as before) */}
      {showNotes && (
        <div className="absolute top-20 right-0 w-80 bg-gray-800 shadow-2xl border border-gray-700 rounded-lg z-50 flex flex-col overflow-hidden transform transition-all" style={{ height: '500px' }}>
          <div className="bg-yellow-400 p-3 flex justify-between items-center">
            <h4 className="font-bold text-yellow-900 flex items-center gap-2"><StickyNote size={16} /> Notes History</h4>
            <button onClick={() => setShowNotes(false)} className="text-yellow-900 hover:bg-yellow-500 rounded p-1"><X size={18} /></button>
          </div>
          <div className="bg-gray-700 p-3 border-b border-gray-600">
            <textarea className="w-full bg-gray-600 text-white text-sm p-2 rounded border border-gray-500 focus:outline-none focus:border-yellow-400 resize-none h-20" placeholder="Write a new note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
            <button onClick={handleAddNote} className="w-full mt-2 bg-yellow-500 hover:bg-yellow-400 text-yellow-900 font-bold py-1 rounded text-sm flex items-center justify-center gap-2"><Plus size={14} /> Add Note</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-800">
            {notesList.length === 0 ? <p className="text-gray-500 text-xs text-center italic mt-10">No notes yet.</p> : notesList.map((note) => (
              <div key={note.id} className="bg-gray-700 p-3 rounded border border-gray-600 relative group hover:border-gray-500 transition">
                {editingNoteId === note.id ? (
                  <div className="flex flex-col gap-2">
                    <textarea className="w-full bg-gray-800 text-white text-sm p-2 rounded border border-gray-500 resize-none h-20 focus:outline-none" value={editedNoteContent} onChange={(e) => setEditedNoteContent(e.target.value)} />
                    <div className="flex justify-end gap-2"><button onClick={cancelEditNote} className="text-xs text-gray-400 hover:text-white">Cancel</button><button onClick={saveEditedNote} className="bg-green-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1"><CheckCircle size={12} /> Save</button></div>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap mb-3">{note.note_content}</p>
                    <div className="flex justify-between items-center border-t border-gray-600 pt-2 mt-2">
                      <span className="text-xs font-bold text-yellow-500 flex items-center gap-1"><Clock size={12} /> {formatNoteDate(note.created_at)}</span>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition"><button onClick={() => startEditNote(note)} className="text-blue-400 hover:text-blue-300"><Edit size={14} /></button><button onClick={() => handleDeleteNote(note.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button></div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* REGISTER / EDIT RX MODAL */}
      {showRxModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[600px] p-6 relative">
            <button onClick={() => setShowRxModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
            <h3 className="text-xl font-bold mb-4 text-teal-700">{isEditing ? 'Edit Prescription' : 'Register Prescription'}</h3>
            <form onSubmit={handleSaveRx} className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-4"><input type="text" placeholder="RX Number" className="border p-2 rounded" required value={rxForm.rx_no} onChange={e => setRxForm({ ...rxForm, rx_no: e.target.value })} /><input type="text" placeholder="NDC" className="border p-2 rounded" value={rxForm.ndc} onChange={e => setRxForm({ ...rxForm, ndc: e.target.value })} /><input type="text" placeholder="Auth Ref" className="border p-2 rounded" value={rxForm.auth_ref_number} onChange={e => setRxForm({ ...rxForm, auth_ref_number: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-4"><input type="text" placeholder="Last Ref" className="border p-2 rounded" value={rxForm.last_ref_number} onChange={e => setRxForm({ ...rxForm, last_ref_number: e.target.value })} /><input type="text" placeholder="Drug Name" className="border p-2 rounded col-span-2" required value={rxForm.drug_name} onChange={e => setRxForm({ ...rxForm, drug_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4"><input type="text" placeholder="Strength" className="border p-2 rounded" value={rxForm.strength} onChange={e => setRxForm({ ...rxForm, strength: e.target.value })} /><input type="text" placeholder="Form" className="border p-2 rounded" value={rxForm.form} onChange={e => setRxForm({ ...rxForm, form: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-4"><div><label>Exp</label><input type="date" className="w-full border p-2 rounded" value={rxForm.expiration_date} onChange={e => setRxForm({ ...rxForm, expiration_date: e.target.value })} /></div><div><label>Order</label><input type="date" className="w-full border p-2 rounded" value={rxForm.order_date} onChange={e => setRxForm({ ...rxForm, order_date: e.target.value })} /></div><div><label>Last Fill</label><input type="date" className="w-full border p-2 rounded" value={rxForm.last_fill_date} onChange={e => setRxForm({ ...rxForm, last_fill_date: e.target.value })} /></div></div>
              <div className="grid grid-cols-3 gap-4"><input type="number" placeholder="Qty" className="border p-2 rounded" value={rxForm.quantity} onChange={e => setRxForm({ ...rxForm, quantity: e.target.value })} /><input type="number" placeholder="Days" className="border p-2 rounded" value={rxForm.days_supply} onChange={e => setRxForm({ ...rxForm, days_supply: e.target.value })} /><input type="number" placeholder="Amt" className="border p-2 rounded" value={rxForm.amount} onChange={e => setRxForm({ ...rxForm, amount: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4 pt-2 border-t"><input type="text" placeholder="Prescriber Name" className="border p-2 rounded" value={rxForm.prescriber_name} onChange={e => setRxForm({ ...rxForm, prescriber_name: e.target.value })} /><input type="text" placeholder="Ins Code" className="border p-2 rounded" value={rxForm.ins_code} onChange={e => setRxForm({ ...rxForm, ins_code: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4"><input type="text" placeholder="Phone" className="border p-2 rounded" value={rxForm.prescriber_phone} onChange={e => setRxForm({ ...rxForm, prescriber_phone: e.target.value })} /></div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 font-bold mb-1">Status</label>
                  <select className="w-full border p-2 rounded bg-white text-gray-700" value={rxForm.status} onChange={e => setRxForm({ ...rxForm, status: e.target.value })}>
                    {STATUS_OPTIONS.map((status, index) => (
                      <option key={index} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded mt-2 hover:bg-teal-700">{isEditing ? 'Update' : 'Register'}</button>
            </form>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && rxToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
            <AlertTriangle className="h-6 w-6 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold">Confirm Deletion</h3>
            <p className="text-gray-500 mt-2 mb-6">Delete <strong>{rxToDelete.drug_name}</strong>?</p>
            <div className="flex justify-center gap-3"><button onClick={() => setShowDeleteModal(false)} className="border px-4 py-2 rounded">Cancel</button><button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded">Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientProfile;